// src/hooks/useViewPreference.js
import { useRef, useState } from 'react'

export function useViewPreference(key, defaultView = 'card') {
  const [view, setViewState] = useState(() => {
    return localStorage.getItem(`sva_view_${key}`) || defaultView
  })
  // Latest value, readable synchronously — `view` is a render-scoped closure, so two setView
  // calls inside ONE render would both resolve their updater against the same stale value.
  const latest = useRef(view)
  latest.current = view

  // ⚠️ v may be an UPDATER FUNCTION — callers use the React setState idiom (the Desk's mobile
  // map pill: `changeEntriesView(v => v === 'map' ? 'gallery' : 'map')`). Resolving it BEFORE
  // the write is load-bearing: `localStorage.setItem(key, fn)` stringifies the function's
  // SOURCE, so the stored preference became "v => v === 'map' ? …" — a value matching no view
  // name. Every guard that tests `view === 'card'` then silently missed, and the Desk fell
  // through to its default branch (2026-07-25: one tap of the mobile map pill and back left
  // phones stuck in the desktop left-thumb card form forever, with no mobile control to leave
  // it). Resolved against the ref rather than inside a setState updater on purpose: persisting
  // from an updater makes the write run twice under StrictMode.
  function setView(v) {
    const next = typeof v === 'function' ? v(latest.current) : v
    latest.current = next
    localStorage.setItem(`sva_view_${key}`, next)
    setViewState(next)
  }

  return [view, setView]
}
